import { useEffect, useState, useRef, useCallback } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

import { ReactComponent as RotateLeft } from '@assets/icons/pdf/rotateLeft.svg';
import { ReactComponent as RotateRight } from '@assets/icons/pdf/rotateRight.svg';
import { ReactComponent as PrintIcon } from '@assets/icons/pdf/print.svg';
import { ReactComponent as FullscreenIcon } from '@assets/icons/pdf/fullscreen.svg';
import { ReactComponent as ExitScreenIcon } from '@assets/icons/pdf/exitScreen.svg';

import CustomCircularProgress from '@/components/CustomCircularProgress';
import { toastError, toastSuccess } from '@/components/Toast';
import CommonImage from '@/components/CommonImage';
import ModalContainer from '@/components/modals/ModalContainer';

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const ZOOM_LEVELS = [
	0.25, 0.33, 0.5, 0.67, 0.75, 0.8, 0.9, 1.0, 1.1, 1.25, 1.5, 1.75, 2.0, 2.5, 3.0, 4.0, 5.0
];

const PdfViewModal = ({ onClose, fileUrl, isShowSidePanel = true, modalTitle }) => {
	const [pdfFile, setPdfFile] = useState(null);
	const [progress, setProgress] = useState(0);
	const [numPages, setNumPages] = useState(null);
	const [currentPage, setCurrentPage] = useState(1);
	const [scale, setScale] = useState(1.0);
	const [isLoading, setIsLoading] = useState(true);
	const [showSidebar, setShowSidebar] = useState(true);
	const [rotation, setRotation] = useState(0);
	const [fitMode, setFitMode] = useState('page');
	const [isFullscreen, setIsFullscreen] = useState(false);
	const [showSearch, setShowSearch] = useState(false);

	const containerRef = useRef(null);
	const pageRefs = useRef({});
	const searchInputRef = useRef(null);
	const isNavigatingRef = useRef(false);

	const loadFile = async () => {
		try {
			setProgress(0);
			setIsLoading(true);
			const res = await fetch(fileUrl);
			const contentLength = res.headers.get('content-length');
			const total = parseInt(contentLength, 10);
			let loaded = 0;

			const reader = res.body.getReader();
			const chunks = [];

			while (true) {
				const { done, value } = await reader.read();

				if (done) break;

				chunks.push(value);
				loaded += value.length;

				if (total) {
					const countProgress = Math.round((loaded / total) * 100);
					setProgress(countProgress);
				}
			}
			const blob = new Blob(chunks, { type: 'application/pdf' });
			const url = URL.createObjectURL(blob);
			setPdfFile(url);
			setProgress(100);
		} catch {
			toastError('Error loading file');
			setIsLoading(false);
		}
	};

	const onDocumentLoadSuccess = ({ numPages }) => {
		setNumPages(numPages);
		setIsLoading(false);
	};

	const onDocumentLoadError = () => {
		toastError('Error loading PDF document');
		setIsLoading(false);
	};

	// Handle fit modes
	const handleFitMode = (mode) => {
		setFitMode(mode);
		switch (mode) {
			case 'page':
				setScale(1.0);
				break;
			case 'actual':
				setScale(1.0);
				break;
		}
	};

	// Chrome-like zoom controls
	const setSpecificZoom = (zoomLevel) => {
		setScale(zoomLevel);
		setFitMode('actual');
	};

	const zoomIn = () => {
		const currentIndex = ZOOM_LEVELS.findIndex((level) => level >= scale);
		const nextIndex = Math.min(currentIndex + 1, ZOOM_LEVELS.length - 1);
		setSpecificZoom(ZOOM_LEVELS[nextIndex]);
	};

	const zoomOut = () => {
		const currentIndex = ZOOM_LEVELS.findIndex((level) => level >= scale);
		const prevIndex = Math.max(currentIndex - 1, 0);
		setSpecificZoom(ZOOM_LEVELS[prevIndex]);
	};

	// Page navigation
	const goToPrevPage = () => {
		if (currentPage > 1) {
			const prevPage = currentPage - 1;
			setCurrentPage(prevPage);
			scrollToPage(prevPage);
		}
	};

	const goToNextPage = () => {
		if (currentPage < numPages) {
			const nextPage = currentPage + 1;
			setCurrentPage(nextPage);
			scrollToPage(nextPage);
		}
	};

	const scrollToPage = (pageNumber) => {
		const pageElement = pageRefs.current[pageNumber];
		if (pageElement && containerRef.current) {
			isNavigatingRef.current = true;
			pageElement.scrollIntoView({
				behavior: 'smooth',
				block: 'start'
			});

			// Reset navigation flag after scroll animation completes
			setTimeout(() => {
				isNavigatingRef.current = false;
			}, 800); // Slightly longer than typical scroll animation
		}
	};

	const goToPage = (pageNumber) => {
		if (pageNumber >= 1 && pageNumber <= numPages) {
			setCurrentPage(pageNumber);
			scrollToPage(pageNumber);
		}
	};

	// Rotation
	const rotateClockwise = () => {
		setRotation((prev) => (prev + 90) % 360);
	};

	const rotateCounterClockwise = () => {
		setRotation((prev) => (prev - 90 + 360) % 360);
	};

	// Fullscreen
	const toggleFullscreen = () => {
		if (!document.fullscreenElement) {
			document.documentElement.requestFullscreen();
			setIsFullscreen(true);
		} else {
			document.exitFullscreen();
			setIsFullscreen(false);
		}
	};

	// Download PDF
	const downloadPdf = () => {
		const link = document.createElement('a');
		link.href = fileUrl;
		link.download = modalTitle || 'document.pdf';
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
		toastSuccess('Download started');
	};

	// Print PDF
	const printPdf = () => {
		window.print();
	};

	// Search functionality
	const toggleSearch = () => {
		setShowSearch(!showSearch);
		if (!showSearch) {
			setTimeout(() => searchInputRef.current?.focus(), 100);
		}
	};

	// Keyboard shortcuts
	const handleKeyDown = useCallback(
		(e) => {
			// Don't trigger shortcuts when typing in input fields
			if (e.target.tagName === 'INPUT') return;

			switch (e.key) {
				case 'ArrowLeft':
					e.preventDefault();
					goToPrevPage();
					break;
				case 'ArrowRight':
					e.preventDefault();
					goToNextPage();
					break;
				case 'ArrowUp':
					e.preventDefault();
					if (containerRef.current) {
						containerRef.current.scrollTop -= 50;
					}
					break;
				case 'ArrowDown':
					e.preventDefault();
					if (containerRef.current) {
						containerRef.current.scrollTop += 50;
					}
					break;
				case 'Home':
					e.preventDefault();
					goToPage(1);
					break;
				case 'End':
					e.preventDefault();
					goToPage(numPages);
					break;
				case 'PageUp':
					e.preventDefault();
					goToPrevPage();
					break;
				case 'PageDown':
					e.preventDefault();
					goToNextPage();
					break;
				case '+':
				case '=':
					if (e.ctrlKey || e.metaKey) {
						e.preventDefault();
						zoomIn();
					}
					break;
				case '-':
					if (e.ctrlKey || e.metaKey) {
						e.preventDefault();
						zoomOut();
					}
					break;
				case '0':
					if (e.ctrlKey || e.metaKey) {
						e.preventDefault();
						handleFitMode('page');
					}
					break;
				case 'f':
					if (e.ctrlKey || e.metaKey) {
						e.preventDefault();
						toggleSearch();
					}
					break;
				case 'p':
					if (e.ctrlKey || e.metaKey) {
						e.preventDefault();
						printPdf();
					}
					break;
				case 'Escape':
					if (showSearch) {
						setShowSearch(false);
					} else if (isFullscreen) {
						toggleFullscreen();
					}
					break;
				case 'F11':
					e.preventDefault();
					toggleFullscreen();
					break;
			}
		},
		[currentPage, numPages, showSearch, isFullscreen]
	);

	// Detect current page based on scroll position
	const handleScroll = () => {
		if (!containerRef.current || !numPages || isNavigatingRef.current) return;

		const container = containerRef.current;
		const containerTop = container.scrollTop;
		const containerHeight = container.clientHeight;
		const centerPosition = containerTop + containerHeight / 2;

		// Find which page is currently in the center of the viewport
		for (let i = 1; i <= numPages; i++) {
			const pageElement = pageRefs.current[i];
			if (pageElement) {
				const pageTop = pageElement.offsetTop;
				const pageBottom = pageTop + pageElement.offsetHeight;

				if (centerPosition >= pageTop && centerPosition <= pageBottom) {
					setCurrentPage(i);
					break;
				}
			}
		}
	};

	const toggleSidebar = () => {
		setShowSidebar(!showSidebar);
	};

	// Render page thumbnails for sidebar
	const renderPageThumbnails = () => {
		if (!numPages || !pdfFile) return null;

		return Array.from(new Array(numPages), (_el, index) => {
			const pageNumber = index + 1;
			const isActive = currentPage === pageNumber;

			return (
				<div
					key={`thumbnail_${pageNumber}`}
					className={`single-pdf-thumbnail ${isActive ? 'active' : ''}`}
					onClick={() => goToPage(pageNumber)}
				>
					<div className="pointer-events-none">
						<Page
							pageNumber={pageNumber}
							height={400}
							scale={0.2}
							rotate={rotation}
							loading={<div className="thumbnail-loading" />}
							error={
								<div className="thumbnail-error d-flex justify-content-center align-items-center">
									Error
								</div>
							}
							renderTextLayer={false}
							renderAnnotationLayer={false}
						/>
					</div>
					<div className="page-count text-center text-white">{pageNumber}</div>
				</div>
			);
		});
	};

	// Render all pages
	const renderAllPages = () => {
		if (!numPages) return null;

		return Array.from(new Array(numPages), (el, index) => {
			const pageNumber = index + 1;
			const isActive = currentPage === pageNumber;

			return (
				<div
					key={`page_${pageNumber}`}
					ref={(el) => (pageRefs.current[pageNumber] = el)}
					className={isActive ? 'pdf-page-active' : 'pdf-page-inactive'}
				>
					<div className="text-center mb-2">
						<small className="text-muted">Page {pageNumber}</small>
					</div>
					<Page
						pageNumber={pageNumber}
						scale={scale}
						rotate={rotation}
						loading={<div className="text-center">Loading page {pageNumber}...</div>}
						error={
							<div className="text-center text-danger">Failed to load page {pageNumber}.</div>
						}
						renderTextLayer={true}
						renderAnnotationLayer={true}
					/>
				</div>
			);
		});
	};

	useEffect(() => {
		loadFile();

		return () => {
			if (pdfFile) {
				URL.revokeObjectURL(pdfFile);
			}
		};
	}, [fileUrl]);

	// Add scroll listener
	useEffect(() => {
		const container = containerRef.current;
		if (container) {
			container.addEventListener('scroll', handleScroll);
			return () => container.removeEventListener('scroll', handleScroll);
		}
	}, [numPages]);

	// Add keyboard listener
	useEffect(() => {
		document.addEventListener('keydown', handleKeyDown);
		return () => document.removeEventListener('keydown', handleKeyDown);
	}, [handleKeyDown]);

	// Handle fullscreen change
	useEffect(() => {
		const handleFullscreenChange = () => {
			setIsFullscreen(!!document.fullscreenElement);
		};

		document.addEventListener('fullscreenchange', handleFullscreenChange);
		return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
	}, []);

	return (
		<ModalContainer className="pdf-view-modal width-1200-modal">
			<div
				className="common-popup w-100 position-relative d-flex flex-column"
				style={{ height: '100vh' }}
			>
				<div className="common-popup-header d-flex justify-content-between">
					<div className="popup-tilte d-flex align-items-center">
						<h3 className="semibold-16 mb-0">{modalTitle}</h3>
						{numPages && (
							<span className="ml-3 regular-14 gray_dark_02">
								{numPages} page{numPages > 1 ? 's' : ''}
							</span>
						)}
					</div>
					<div className="close-btn">
						<CommonImage src="/icons/close.svg" alt="close" onClick={onClose} />
					</div>
				</div>

				{/* Toolbar */}
				{pdfFile && !isLoading && (
					<div className="pdf-toolbar">
						{/* Left Section - Navigation */}
						<div className="pdf-toolbar-icons d-flex align-items-center">
							{isShowSidePanel && (
								<button
									className="button text mr-2"
									onClick={toggleSidebar}
									title={showSidebar ? 'Hide Sidebar' : 'Show Sidebar'}
								>
									<svg
										xmlns="http://www.w3.org/2000/svg"
										width="24"
										height="24"
										viewBox="0 0 32 32"
									>
										<path
											fill="currentColor"
											d="M3 7v2h26V7zm0 4v2h26v-2zm0 4v2h26v-2zm0 4v2h26v-2zm0 4v2h26v-2z"
										/>
									</svg>
								</button>
							)}

							<button
								className="button text mr-1"
								onClick={goToPrevPage}
								disabled={currentPage <= 1}
								title="Previous Page (←)"
							>
								<CommonImage src="/icons/Leftarrwo-white.svg" alt="previous" />
							</button>

							<div className="d-flex align-items-center mr-2">
								<span className="mx-2 regular-14 text-white">
									{currentPage}/{numPages}
								</span>
							</div>

							<button
								className="button text mr-2"
								onClick={goToNextPage}
								disabled={currentPage >= numPages}
								title="Next Page (→)"
							>
								<CommonImage src="/icons/Rightarrow-white.svg" alt="next" />
							</button>
						</div>

						{/* Center Section - Zoom */}
						<div className="pdf-toolbar-icons  d-flex align-items-center">
							<button
								className="button text mr-1"
								onClick={zoomOut}
								disabled={scale <= ZOOM_LEVELS[0]}
								title="Zoom Out (Ctrl+-)"
							>
								<CommonImage src="/icons/minus-white.svg" />
							</button>

							<select
								className="mx-1"
								value={fitMode === 'actual' ? Math.round(scale * 100) : fitMode}
								onChange={(e) => {
									const value = e.target.value;
									if (value === 'page') {
										handleFitMode(value);
									} else {
										setSpecificZoom(parseInt(value) / 100);
									}
								}}
							>
								{ZOOM_LEVELS.map((level) => (
									<option key={level} value={Math.round(level * 100)}>
										{Math.round(level * 100)}%
									</option>
								))}
							</select>

							<button
								className="button text ml-1"
								onClick={zoomIn}
								disabled={scale >= ZOOM_LEVELS[ZOOM_LEVELS.length - 1]}
								title="Zoom In (Ctrl++)"
							>
								<CommonImage src="/icons/add_white.svg" />
							</button>
						</div>

						{/* Right Section - Actions */}
						<div className="pdf-toolbar-icons d-flex align-items-center">
							<button
								className="button text mr-1"
								onClick={rotateCounterClockwise}
								title="Rotate Counterclockwise"
							>
								<RotateLeft />
							</button>

							<button
								className="button text mr-1"
								onClick={rotateClockwise}
								title="Rotate Clockwise"
							>
								<RotateRight />
							</button>

							<button className="button text mr-1" onClick={printPdf} title="Print (Ctrl+P)">
								<PrintIcon />
							</button>

							<button className="button text mr-1" onClick={downloadPdf} title="Download">
								<CommonImage src="/icons/download-gray-dark-02.svg" />
							</button>

							<button
								className="button text"
								onClick={toggleFullscreen}
								title={isFullscreen ? 'Exit Fullscreen (F11)' : 'Fullscreen (F11)'}
							>
								{isFullscreen ? <FullscreenIcon /> : <ExitScreenIcon />}
							</button>
						</div>
					</div>
				)}

				{/* Loading Overlay */}
				{(progress < 100 || isLoading) && (
					<div className="pdf-loading-overlay">
						<CustomCircularProgress progress={progress} size={120} strokeWidth={10} />
					</div>
				)}

				{/* Main Content Area */}
				<div className="pdf-body-container d-flex">
					{/* Sidebar with Page Thumbnails */}
					{showSidebar && pdfFile && !isLoading && (
						<div className="pdf-sidebar">
							<div className="text-center mb-3">
								<small className="text-white regular-12">Pages</small>
							</div>
							<Document file={pdfFile}>{renderPageThumbnails()}</Document>
						</div>
					)}

					{/* PDF Viewer */}
					<div ref={containerRef} className="pdf-container d-flex flex-column align-items-center">
						{pdfFile && (
							<Document
								file={pdfFile}
								onLoadSuccess={onDocumentLoadSuccess}
								onLoadError={onDocumentLoadError}
								error={
									<div className="text-center">
										<p className="text-danger">Failed to load PDF file.</p>
										<button className="btn btn-primary" onClick={loadFile}>
											Retry
										</button>
									</div>
								}
							>
								{renderAllPages()}
							</Document>
						)}
					</div>
				</div>
			</div>
		</ModalContainer>
	);
};

export default PdfViewModal;
